import clsx from "clsx";
import moment from "moment";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdCheck, MdClose } from "react-icons/md";
import { updateTransaction } from "src/api/transactions";
import Modal from "src/components/Modal/Modal";
import RadioGroup from "src/components/RadioGroup/RadioGroup";
import CustomSelect from "src/components/Select/CustomSelect";
import TextInput from "src/components/TextInput/TextInput";
import { objectHasChanges, showNotification } from "src/helpers/utils";
import useCache from "src/hooks/useCache";
import ChangesPendingToSave from "../ChangesPendingToSave/ChangesPendingToSave";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

interface IRecurringTransactionSettings {
  onClose: () => void;
  onSave: () => void;
  transaction: string;
  currentSettings: any;
}

export enum ERecurringTransactionFrequency {
  ANNUAL = "ANNUAL",
  BIANNUAL = "BIANNUAL",
  QUARTER = "QUARTER",
  MONTHLY = "MONTHLY",
  FORTNIGHT = "FORTNIGHT",
  WEEKLY = "WEEKLY",
  CUSTOM = "CUSTOM",
  // FIXED = "FIXED",
}

const RecurringTransactionSettings: React.FC<IRecurringTransactionSettings> = ({
  onClose,
  transaction,
  onSave,
  currentSettings = {},
}) => {
  const { t } = useTranslation(["modals", "common"]);

  const frequencyOptions = useMemo(
    () => [
      // {
      //   value: ERecurringTransactionFrequency.FIXED,
      //   label: "Fecha fija",
      // },
      {
        value: ERecurringTransactionFrequency.WEEKLY,
        label: t("modals:recurrence_settings.frequency.options.weekly"),
      },
      {
        value: ERecurringTransactionFrequency.FORTNIGHT,
        label: t("modals:recurrence_settings.frequency.options.biweekly"),
      },
      {
        value: ERecurringTransactionFrequency.MONTHLY,
        label: t("modals:recurrence_settings.frequency.options.monthly"),
      },
      {
        value: ERecurringTransactionFrequency.QUARTER,
        label: t("modals:recurrence_settings.frequency.options.quarterly"),
      },
      {
        value: ERecurringTransactionFrequency.BIANNUAL,
        label: t("modals:recurrence_settings.frequency.options.semiannually"),
      },
      {
        value: ERecurringTransactionFrequency.ANNUAL,
        label: t("modals:recurrence_settings.frequency.options.annually"),
      },
      {
        value: ERecurringTransactionFrequency.CUSTOM,
        label: t("modals:recurrence_settings.frequency.options.custom"),
      },
    ],
    [t],
  );

  const [selectedFrequency, setSelectedFrequency] =
    useState<ERecurringTransactionFrequency>(
      currentSettings?.settings?.frequency ||
        ERecurringTransactionFrequency.MONTHLY,
    );
  const [nextPaymentDate, setNextPaymentDate] = useState<string>(
    currentSettings?.nextPayment || "",
  );
  const [customInterval, setCustomInterval] = useState<string>(
    currentSettings?.settings?.customInterval || 0,
  );
  const [isEnabled, setIsEnabled] = useState<boolean>(
    !!currentSettings?.enabled,
  );
  const [isUpdatingSettings, setIsUpdatingSettings] = useState<boolean>(false);
  const { clearCache } = useCache();

  const updateRecurringSettings = async () => {
    setIsUpdatingSettings(true);
    try {
      const result = await updateTransaction({
        transactionId: transaction || "",
        fieldsToUpdate: {
          recurring: {
            settings: {
              frequency: selectedFrequency,
              customInterval,
              createdAt: moment().format("yyyy-MM-DD"),
              disabledAt: moment().format("yyyy-MM-DD"),
            },
            enabled: isEnabled,
            nextPayment: moment(nextPaymentDate).format("yyyy-MM-DD"),
          },
        },
      });

      if (result?.success) {
        await clearCache();

        setIsUpdatingSettings(false);
        onSave();

        showNotification({
          message: "Cambios guardados",
          kind: "success",
        });
      } else {
        setIsUpdatingSettings(false);
        showNotification({
          message: "Error guardando los cambios",
          kind: "error",
        });
      }
    } catch (e) {
      setIsUpdatingSettings(false);
      showNotification({
        message: "Error guardando los cambios",
        kind: "error",
      });
    }
  };

  const saveIsEnabled = useMemo(() => {
    if (isEnabled) {
      return nextPaymentDate && selectedFrequency;
    }

    return true;
  }, [isEnabled, nextPaymentDate, selectedFrequency]);

  const hasChanges = useMemo(() => {
    return objectHasChanges({
      original: {
        enabled: !!currentSettings?.enabled,
        frequency: currentSettings?.settings?.frequency || "",
        nextPayment: currentSettings?.nextPayment || "",
        customInterval: currentSettings?.settings?.customInterval || 0,
      },
      edited: {
        enabled: isEnabled,
        frequency: selectedFrequency,
        nextPayment: nextPaymentDate,
        customInterval,
      },
    });
  }, [
    currentSettings,
    isEnabled,
    selectedFrequency,
    nextPaymentDate,
    customInterval,
  ]);

  return (
    <Modal onEscape={onClose} className="md:!max-w-[530px] md:w-full">
      <Modal.Header
        title={t("modals:recurrence_settings.title")}
        onClose={onClose}
      />
      <Modal.Body>
        <div className={clsx(["flex", "flex-col", "gap-6", "pb-2"])}>
          <div className={clsx(["text-sm", "md:text-sm"])}>
            Configura la transacción como frecuente para recibir notificaciones
            cuando el próximo pago se acerque.
          </div>
          <div>
            <RadioGroup
              onChange={(value: any) => {
                setIsEnabled(value);
              }}
              columns={2}
              selected={isEnabled}
              options={[
                {
                  value: true,
                  label: t("modals:recurrence_settings.status.enabled"),
                },
                {
                  value: false,
                  label: t("modals:recurrence_settings.status.disabled"),
                },
              ]}
            />
          </div>
          {isEnabled && (
            <>
              <div
                className={clsx([
                  "grid",
                  "w-full",
                  "gap-6",
                  "md:gap-4",
                  "grid-cols-1",
                  "md:grid-cols-2",
                ])}
              >
                <CustomSelect
                  options={frequencyOptions}
                  label={t("modals:recurrence_settings.frequency.label")}
                  placeholder={t(
                    "modals:recurrence_settings.frequency.placeholder",
                  )}
                  required
                  onChange={(e) => {
                    setSelectedFrequency(e?.value);
                  }}
                  value={selectedFrequency}
                  useValueAsLabel={false}
                  isSearchable={false}
                />
                {selectedFrequency === ERecurringTransactionFrequency.CUSTOM ? (
                  <TextInput
                    value={customInterval}
                    onChange={(e: any) => setCustomInterval(e?.target?.value)}
                    required
                    type="number"
                    step={1}
                    placeholder="Intervalo en días"
                    label="Intervalo personalizado en días"
                  />
                ) : (
                  <TextInput
                    value={nextPaymentDate}
                    onChange={(e: any) => setNextPaymentDate(e?.target?.value)}
                    required
                    type="date"
                    placeholder={t(
                      "modals:recurrence_settings.next_transaction.placeholder",
                    )}
                    label={t(
                      "modals:recurrence_settings.next_transaction.label",
                    )}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        leftContent={<ChangesPendingToSave show={hasChanges} />}
        actions={[
          {
            text: t("common:buttons.save"),
            onClick: async () => await updateRecurringSettings(),
            kind: EButtonKind.SAVE,
            icon: <MdCheck size={ICON_SIZES.SM} />,
            disabled: isUpdatingSettings || !saveIsEnabled || !hasChanges,
            isLoading: isUpdatingSettings,
          },
          {
            text: t("common:buttons.close"),
            onClick: onClose,
            kind: EButtonKind.NORMAL,
            icon: <MdClose size={ICON_SIZES.SM} />,
            disabled: isUpdatingSettings,
          },
        ]}
      />
    </Modal>
  );
};

export default RecurringTransactionSettings;
